import { motion } from "framer-motion";
import {
    FiCode,
    FiHeart,
    FiUsers,
    FiTarget,
    FiZap,
    FiShield,
    FiGlobe,
    FiMail,
} from "react-icons/fi";
import { useLanguage } from "../contexts/LanguageContext";

const About = () => {
    const { t } = useLanguage();

    const stats = [
        { label: t("about.stats.tools"), value: "2+", icon: FiCode },
        { label: t("about.stats.languages"), value: "4", icon: FiGlobe },
        { label: t("about.stats.users"), value: "100+", icon: FiUsers },
        { label: t("about.stats.uptime"), value: "99.9%", icon: FiZap },
    ];

    const values = [
        {
            icon: FiTarget,
            title: t("about.values.innovation.title"),
            description: t("about.values.innovation.desc"),
        },
        {
            icon: FiHeart,
            title: t("about.values.userFirst.title"),
            description: t("about.values.userFirst.desc"),
        },
        {
            icon: FiShield,
            title: t("about.values.privacy.title"),
            description: t("about.values.privacy.desc"),
        },
    ];

    return (
        <div className="space-y-16">
            {/* Hero Section */}
            <section className="text-center space-y-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    <h1 className="text-4xl md:text-6xl font-bold bg-gradient-to-r from-purple-600 via-blue-600 to-cyan-600 bg-clip-text text-transparent mb-6">
                        {t("about.title")}
                    </h1>
                    <p className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
                        {t("about.subtitle")}
                    </p>
                </motion.div>
            </section>

            {/* Stats Section */}
            <section className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                    <motion.div
                        key={stat.label}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: index * 0.1 }}
                        className="text-center p-6 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700"
                    >
                        <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl mb-4">
                            <stat.icon className="w-6 h-6" />
                        </div>
                        <div className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                            {stat.value}
                        </div>
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            {stat.label}
                        </div>
                    </motion.div>
                ))}
            </section>

            {/* Mission Section */}
            <section className="space-y-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                    className="text-center"
                >
                    <h2 className="text-3xl font-bold mb-6">
                        {t("about.mission.title")}
                    </h2>
                    <p className="text-lg text-gray-600 dark:text-gray-400 max-w-4xl mx-auto leading-relaxed">
                        {t("about.mission.description")}
                    </p>
                </motion.div>
            </section>

            {/* Values Section */}
            <section className="space-y-8">
                <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                    className="text-3xl font-bold text-center"
                >
                    {t("about.values.title")}
                </motion.h2>

                <div className="grid md:grid-cols-2 gap-8">
                    {values.map((value, index) => (
                        <motion.div
                            key={value.title}
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6, delay: index * 0.1 }}
                            viewport={{ once: true }}
                            className="p-6 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700"
                        >
                            <div className="flex items-center space-x-4 mb-4">
                                <div className="p-3 bg-gradient-to-r from-purple-500 to-blue-500 text-white rounded-xl">
                                    <value.icon className="w-6 h-6" />
                                </div>
                                <h3 className="text-xl font-semibold">
                                    {value.title}
                                </h3>
                            </div>
                            <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                                {value.description}
                            </p>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* Contact Section */}
            <section className="text-center space-y-6 p-8 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 to-blue-900/20 rounded-3xl">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    viewport={{ once: true }}
                >
                    <h2 className="text-2xl font-bold mb-4">
                        {t("about.contact.title")}
                    </h2>
                    <p className="text-gray-600 dark:text-gray-400 mb-6">
                        {t("about.contact.description")}
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
                        <a
                            href="mailto:mewseeds@gmail.com"
                            className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-xl font-semibold hover:shadow-xl hover:scale-105 transition-all duration-300"
                        >
                            <FiMail className="w-5 h-5" />
                            <span>mewseeds@gmail.com</span>
                        </a>
                    </div>
                </motion.div>
            </section>
        </div>
    );
};

export default About;
